import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from "swr";
import { fetchData } from "../../../lib/dashboard/data";
import { useLocation, useSearchParams } from "react-router-dom";

const DerivativesStrategiesContext = createContext();

const StrategiesProvider = ({ children }) => {

    const [selectedRiskLevel, setSelectedRiskLevel] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState([]);
    const [isMoreThanOneWeekChecked, setIsMoreThanOneWeekChecked] = useState(true);
    const [isRemoveAppliedPriceChecked, setIsRemoveAppliedPriceChecked] = useState(true);
    const [selectedSymbol, setSelectedSymbol] = useState("همه");
    const [selectedRowDetails, setSelectedRowDetails] = useState({
        actions: [],
        coordinates: [],
        profit_factor: null,
        base_equity_last_price: null,
    });
    const [chartData, setChartData] = useState({
        priceChartData: [],
        percentChartData: [],
    })
    const [searchParams] = useSearchParams();
    const tableParams = searchParams.get("table");
    const { pathname } = useLocation();

    const basePath = (() => {
        switch (true) {
            case pathname.includes("commodity-market"):
                return "/future/strategies";
            case pathname.includes("stock-option"):
                return "/option/v2/strategies";
            default:
                return null;
        }
    })();

    const baseTablePath = (() => {
        switch (true) {
            case pathname.includes("commodity-market"):
                return "/future/options/";
            case pathname.includes("stock-option"):
                return "/option/v2/positions/";
            default:
                return null;
        }
    })();

    //GET NO RISK STRATEGY
    const noRiskStrategies = useSWR(
        `${basePath}/no_risk/all_profit`,
        fetchData,
        {
            revalidateOnFocus: false,
            dedupingInterval: 6000,
        },
    )

    //GET LOW RISK STRATEGY
    const lowRiskStrategies = useSWR(
        `${basePath}/low_risk/all_profit`,
        fetchData,
        {
            revalidateOnFocus: false,
            dedupingInterval: 6000,
        },
    )

    //GET HIGH RISK STRATEGY
    const highRiskStrategies = useSWR(
        `${basePath}/high_risk/all_profit`,
        fetchData,
        {
            revalidateOnFocus: false,
            dedupingInterval: 6000,
        },
    )

    const strategies = [
        { name: "پر ریسک", key: "high_risk", data: highRiskStrategies },
        { name: "کم ریسک", key: "low_risk", data: lowRiskStrategies },
        { name: "بدون ریسک", key: "no_risk", data: noRiskStrategies },
    ];

    const strategyTableData = useSWR(
        selectedRiskLevel?.key && selectedStrategy?.key
            ? [`${baseTablePath}${selectedRiskLevel.key}/${selectedStrategy.key}`, `table=${tableParams}`]
            : null,
        ([url, params]) => fetchData(url, params),
        {
            revalidateOnFocus: false,
            dedupingInterval: 6000,
        },
    );

    const filterData = useMemo(() => {
        if (!strategyTableData?.data) return [];

        let filteredData = strategyTableData?.data;

        if (isMoreThanOneWeekChecked) {
            filteredData = filteredData.filter((item) => item.remained_day > 7);
        }

        if (isRemoveAppliedPriceChecked) {
            filteredData = filteredData.filter((item) => item.strike_price_deviation < 0.15);
        }

        if (selectedSymbol !== "همه") {
            filteredData = filteredData.filter((item) => item.base_equity_symbol === selectedSymbol);
        }

        return filteredData;
    }, [isMoreThanOneWeekChecked, isRemoveAppliedPriceChecked, strategyTableData?.data, selectedSymbol]);

    const filteredTableData = {
        ...strategyTableData,
        data: filterData,
    };

    return (
        <DerivativesStrategiesContext.Provider
            value={{
                strategyTableData,
                isMoreThanOneWeekChecked,
                setIsMoreThanOneWeekChecked,
                isRemoveAppliedPriceChecked,
                setIsRemoveAppliedPriceChecked,
                filteredTableData,
                strategies,
                noRiskStrategies,
                lowRiskStrategies,
                highRiskStrategies,
                selectedRiskLevel,
                setSelectedRiskLevel,
                selectedStrategy,
                setSelectedStrategy,
                selectedSymbol,
                setSelectedSymbol,
                selectedRowDetails,
                setSelectedRowDetails,
                chartData,
                setChartData,
            }}
        >
            {children}
        </DerivativesStrategiesContext.Provider>
    );
};

export default StrategiesProvider;

export function useStrategiesContext() {
    return useContext(DerivativesStrategiesContext);
}